import React from "react";
import { CheckmarkIcon } from "./Icons";

// ============================================================================
// FeedbackSuccessBanner — success toast after submitting thumbs-down feedback
// ============================================================================

export function FeedbackSuccessBanner({ language }) {
    return (
        <div className="bg-orange-400 text-white text-center px-3 md:px-4 py-4 md:py-6 my-2 md:my-4 rounded-md font-semibold animate-fadeInOut mt-4 mx-4 md:mx-0">
            <div className="flex items-center justify-center gap-2 md:gap-4 px-1 md:px-2">
                <CheckmarkIcon />
                <span className="text-sm md:text-base">
                    {language === "en"
                        ? "Submitted successfully! Thank you for your feedback."
                        : "已成功提交！ 感謝您的反饋。"}
                </span>
            </div>
        </div>
    );
}

// ============================================================================
// FeedbackModal — modal dialog for submitting thumbs-down feedback with reason
// ============================================================================

export function FeedbackModal({
    language,
    isFeedbackSubmitting,
    setFeedbackReason,
    onClose,
    onSubmit,
    error,
}) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={() => !isFeedbackSubmitting && onClose()}
            />
            <div
                role="dialog"
                aria-modal="true"
                aria-label={language === "zh" ? "請提供反饋" : "Please provide feedback"}
                className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            >
                {/* Header */}
                <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                            <svg
                                className="w-4 h-4 text-orange-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                                />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-900">
                                {language === "en"
                                    ? "Please provide feedback"
                                    : "請提供反饋"}
                            </h3>
                            <p className="text-sm text-gray-500">
                                {language === "en"
                                    ? "Help us improve our responses"
                                    : "幫助我們改善回應"}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={isFeedbackSubmitting}
                        className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-1.5 transition-all disabled:opacity-50"
                        aria-label="Close"
                    >
                        <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <form
                    className="flex flex-col"
                    onSubmit={(e) => {
                        e.preventDefault();
                        onSubmit();
                    }}
                >
                    <div className="p-6">
                        {error && (
                            <p role="alert" className="mb-3 text-sm text-red-600">
                                {error}
                            </p>
                        )}
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            {language === "en" ? "Reason" : "原因"}
                        </label>
                        <textarea
                            required
                            className="w-full h-36 p-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all text-sm text-gray-900 placeholder-gray-400 resize-none"
                            onChange={(e) => setFeedbackReason(e.target.value)}
                            placeholder={
                                language === "en"
                                    ? "Please state the reason..."
                                    : "請說明原因..."
                            }
                            disabled={isFeedbackSubmitting}
                        />
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isFeedbackSubmitting}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all disabled:opacity-50"
                        >
                            {language === "en" ? "Cancel" : "取消"}
                        </button>
                        <button
                            type="submit"
                            disabled={isFeedbackSubmitting}
                            className="px-5 py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-xl shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isFeedbackSubmitting ? (
                                <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></div>
                                    {language === "zh"
                                        ? "提交中..."
                                        : "Submitting..."}
                                </div>
                            ) : language === "en" ? (
                                "Submit Feedback"
                            ) : (
                                "提交反饋"
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
